import styled, { keyframes } from "styled-components";
import { MainInner } from "@/styles/global";
import { GatsbyImage } from "gatsby-plugin-image";

import {
  Body,
  h4,
  h4Css,
  h5,
  h6,
  TextButtonBoldCss,
  TextButtonCss,
  Wysiwyg,
} from "@/styles/typography";

const FundManagerWrapper = styled.div`
  margin: 0 auto;
  margin-bottom: 60px;
  padding: 0 24px;
  max-width: 1300px;

  &:last-child {
    padding: 16px;
    gap: 32px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    padding: 24px;

    &:last-child {
      gap: 40px;
      padding-bottom: 40px;
    }
  }
`;

const FindManagerSplitSection = styled.div`
    display: flex;
    gap: 30px;
    margin-top: 40px;

    @media (max-width: 900px) {
        flex-wrap: wrap;
    }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FactWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 32px;
  background-color: #00278A1A;
  padding: 32px;
  gap: 8px;

  a {
    color: ${({ theme }) => theme.colors.primary};
    border: 1px solid ${({ theme }) => theme.colors.primary};
    flex: 32.8%;
    width: 100%;

    &:last-of-type {
      text-align: center;
    }
  }
`;

const H4 = styled(h4)`
  color: ${({ theme }) => theme.colors.title};
  font-size: 32px;
`;

const H5 = styled(h5)`
  color: ${({ theme }) => theme.colors.title};
  font-size: 20px;
`;

const TrusteesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    gap: 16px;
  }
`;

const WysiwygText = styled(Wysiwyg)`
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-bottom: 8px !important;
    font-size: 20px;
  }
`;

const LoadingKeyframes = keyframes`
from {
  transform: scale(0) translateX(-50%);
}
to {
  transform: scale(1) translateX(-50%);
}
`;

const Loading = styled.div<LoadingProps>`
  animation: ${LoadingKeyframes} 0.6s forwards;
  animation-delay: ${({ $loading }) => ($loading ? "1s" : "0s")};
  position: absolute;
  top: 240px;
  left: 50%;

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    top: 70px;
  }

  .lds-ellipsis,
  .lds-ellipsis div {
    box-sizing: border-box;
  }
  .lds-ellipsis {
    display: inline-block;
    position: relative;
    width: 80px;
    height: 80px;
  }
  .lds-ellipsis div {
    position: absolute;
    top: 33.33333px;
    width: 13.33333px;
    height: 13.33333px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    animation-timing-function: cubic-bezier(0, 1, 1, 0);
  }
  .lds-ellipsis div:nth-child(1) {
    left: 8px;
    animation: lds-ellipsis1 0.6s infinite;
  }
  .lds-ellipsis div:nth-child(2) {
    left: 8px;
    animation: lds-ellipsis2 0.6s infinite;
  }
  .lds-ellipsis div:nth-child(3) {
    left: 32px;
    animation: lds-ellipsis2 0.6s infinite;
  }
  .lds-ellipsis div:nth-child(4) {
    left: 56px;
    animation: lds-ellipsis3 0.6s infinite;
  }
  @keyframes lds-ellipsis1 {
    0% {
      transform: scale(0);
    }
    100% {
      transform: scale(1);
    }
  }
  @keyframes lds-ellipsis3 {
    0% {
      transform: scale(1);
    }
    100% {
      transform: scale(0);
    }
  }
  @keyframes lds-ellipsis2 {
    0% {
      transform: translate(0, 0);
    }
    100% {
      transform: translate(24px, 0);
    }
  }
`;


const Trustee = styled.div`
  display: flex;
  gap: 16px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  background: ${({ theme }) => theme.colors.white};
  padding: 16px;
  min-width: 425px;

    @media (max-width: 576px) {
        min-width: 100%;
        flex-wrap: wrap;
    }
`;

const TrusteeImage = styled(GatsbyImage)`
  border-radius: 5px;
  width: 128px;

  @media (max-width: 576px) {
    width: 100%;
  }

  img {
    object-fit: contain!important;
  }
`;

const TrusteeInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 63%;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    width: 100%;
  }

  @media (min-width: 1400px) {
    width: 63%;
  }
`;

const TrusteeName = styled(h6)`
  color: ${({ theme }) => theme.colors.title};
  font-size: 18px;
`;

const TrusteeText = styled(Body)`
  color: ${({ theme }) => theme.colors.placeholder};
  font-size: 16px;
`;

const FactTitle = styled.span`
  ${TextButtonCss};
  font-weight: 600;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.white};
`;

const Fact = styled.a`
  border-radius: 5px;
  background: var(--Test-Mrkare-bl, #001953);
  padding: 12px;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  svg {
    min-width: 24px;
    min-height: 24px;
  }
`;

const Text = styled(Body)`
  color: ${({ theme }) => theme.colors.placeholder};
`;

const S = {
    FundManagerWrapper,
    FindManagerSplitSection,
    TextContainer,
    H4,
    H5,
    TrusteesWrapper,
    FactWrapper,
    TextWrapper,
    WysiwygText,
    Loading,
    Trustee,
    TrusteeImage,
    TrusteeInfo,
    TrusteeName,
    TrusteeText,
    FactTitle,
    Fact,
    Text
};

export default S;
